import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { siteConfigAPI } from '../services/api';

interface SiteInfo {
  company_name: string;
  company_address: string;
  company_phone: string;
  company_email: string;
  business_hours: string;
}

const ContactPage: React.FC = () => {
  const [info, setInfo] = useState<SiteInfo | null>(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitState, setSubmitState] = useState({ sending: false, sent: false, error: '' });

  useEffect(() => {
    document.title = 'Contact Us | Sourdoughsy';
    siteConfigAPI.getPublicConfig()
      .then(res => setInfo(res.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState(prev => ({ ...prev, error: '', sending: true }));
    try {
      await siteConfigAPI.submitContact(form);
      setSubmitState(prev => ({ ...prev, sent: true }));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      const data = err?.response?.data;
      if (data) {
        const msgs = Object.values(data).flat().join(' ');
        setSubmitState(prev => ({ ...prev, error: msgs || 'Something went wrong. Please try again.' }));
      } else {
        setSubmitState(prev => ({ ...prev, error: 'Something went wrong. Please try again.' }));
      }
    } finally {
      setSubmitState(prev => ({ ...prev, sending: false }));
    }
  };

  const hasContactInfo = info && (info.company_address || info.company_phone || info.company_email || info.business_hours);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar />

      {/* Hero */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          paddingTop: '120px',
          paddingBottom: '48px',
          textAlign: 'center',
        }}
      >
        <Container>
          <h1
            style={{
              color: 'white',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
              letterSpacing: '-0.5px',
              marginBottom: '8px',
            }}
          >
            Contact Us
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', marginBottom: 0, maxWidth: '500px', margin: '0 auto' }}>
            Have a question or feedback? We'd love to hear from you.
          </p>
        </Container>
      </div>

      {/* Content */}
      <div style={{ flex: 1, background: '#f8fafc', padding: '48px 0 80px' }}>
        <Container style={{ maxWidth: '960px' }}>
          <Row className="g-4">
            {/* Contact Form */}
            <Col md={hasContactInfo ? 7 : 12}>
              <div
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: 'clamp(24px, 5vw, 40px)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  height: '100%',
                }}
              >
                {submitState.sent ? (
                  <div className="text-center py-5">
                    <CheckCircleIcon style={{ width: '56px', height: '56px', color: '#10b981', marginBottom: '16px' }} />
                    <h4 style={{ fontWeight: 700, color: '#1f2937' }}>Message Sent!</h4>
                    <p style={{ color: '#64748b', maxWidth: '360px', margin: '0 auto 20px' }}>
                      Thanks for reaching out. We'll get back to you as soon as possible.
                    </p>
                    <Button
                      variant="outline-primary"
                      onClick={() => setSubmitState(prev => ({ ...prev, sent: false }))}
                      style={{ borderRadius: '10px', fontWeight: 600 }}
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h5 style={{ fontWeight: 700, color: '#1f2937', marginBottom: '20px' }}>
                      Send us a message
                    </h5>
                    {submitState.error && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{submitState.error}</Alert>}
                    <Form onSubmit={handleSubmit}>
                      <Row className="g-3">
                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Name</Form.Label>
                            <Form.Control
                              required
                              value={form.name}
                              onChange={e => setForm({ ...form, name: e.target.value })}
                              placeholder="Your name"
                              style={{ borderRadius: '10px', padding: '10px 14px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Email</Form.Label>
                            <Form.Control
                              required
                              type="email"
                              value={form.email}
                              onChange={e => setForm({ ...form, email: e.target.value })}
                              placeholder="you@example.com"
                              style={{ borderRadius: '10px', padding: '10px 14px' }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mt-3">
                        <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Subject</Form.Label>
                        <Form.Control
                          required
                          value={form.subject}
                          onChange={e => setForm({ ...form, subject: e.target.value })}
                          placeholder="What's this about?"
                          style={{ borderRadius: '10px', padding: '10px 14px' }}
                        />
                      </Form.Group>
                      <Form.Group className="mt-3">
                        <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          required
                          value={form.message}
                          onChange={e => setForm({ ...form, message: e.target.value })}
                          placeholder="Tell us more..."
                          style={{ borderRadius: '10px', padding: '10px 14px', resize: 'vertical' }}
                        />
                      </Form.Group>
                      <Button
                        type="submit"
                        disabled={submitState.sending}
                        className="mt-3 d-inline-flex align-items-center gap-2"
                        style={{
                          borderRadius: '10px',
                          fontWeight: 600,
                          padding: '10px 28px',
                          background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                          border: 'none',
                        }}
                      >
                        {submitState.sending ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <PaperAirplaneIcon style={{ width: '16px', height: '16px' }} />
                        )}
                        {submitState.sending ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Form>
                  </>
                )}
              </div>
            </Col>

            {/* Contact Info sidebar */}
            {hasContactInfo && (
              <Col md={5}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: 'clamp(24px, 5vw, 40px)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    height: '100%',
                  }}
                >
                  <h5 style={{ fontWeight: 700, color: '#1f2937', marginBottom: '24px' }}>
                    Get in touch
                  </h5>

                  <div className="d-flex flex-column gap-4">
                    {info.company_email && (
                      <div className="d-flex gap-3">
                        <div
                          style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          <EnvelopeIcon style={{ width: '18px', height: '18px', color: '#3b82f6' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Email</div>
                          <a href={`mailto:${info.company_email}`} style={{ color: '#3b82f6', fontSize: '0.9rem', textDecoration: 'none' }}>
                            {info.company_email}
                          </a>
                        </div>
                      </div>
                    )}

                    {info.company_phone && (
                      <div className="d-flex gap-3">
                        <div
                          style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          <PhoneIcon style={{ width: '18px', height: '18px', color: '#10b981' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Phone</div>
                          <a href={`tel:${info.company_phone}`} style={{ color: '#374151', fontSize: '0.9rem', textDecoration: 'none' }}>
                            {info.company_phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {info.company_address && (
                      <div className="d-flex gap-3">
                        <div
                          style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          <MapPinIcon style={{ width: '18px', height: '18px', color: '#f59e0b' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Address</div>
                          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 0, whiteSpace: 'pre-line' }}>
                            {info.company_address}
                          </p>
                        </div>
                      </div>
                    )}

                    {info.business_hours && (
                      <div className="d-flex gap-3">
                        <div
                          style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          <ClockIcon style={{ width: '18px', height: '18px', color: '#8b5cf6' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Business Hours</div>
                          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 0, whiteSpace: 'pre-line' }}>
                            {info.business_hours}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
